import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_MEDIUM } from '@/lib/motion';
import { TIMELINE_ANNOTATION_BLOCK_CLASS } from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { MeasureTokenText } from '@/components/ui/MeasureTokenText';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type TriggerMode = 'deadline' | 'count';

export interface MealTriggerModeComparisonProps {
  activeMode?: TriggerMode;
  onModeChange?: (mode: TriggerMode) => void;
}

/* ------------------------------------------------------------------ */
/*  Contract phrase data                                               */
/* ------------------------------------------------------------------ */

interface ModeCard {
  mode: TriggerMode;
  title: string;
  phrases: string[];
  summary: string;
}

const MODE_CARDS: ModeCard[] = [
  {
    mode: 'deadline',
    title: 'Deadline',
    phrases: [
      '"break no later than hour X"',
      '"break every X hours"',
    ],
    summary: 'Tracks continuous work time. Surcharge triggers when you hit the deadline without a qualifying break.',
  },
  {
    mode: 'count',
    title: 'Break Count',
    phrases: [
      '"N breaks per shift"',
      '"N meal periods for X hour days"',
    ],
    summary: 'Counts total breaks taken. Missing breaks are assessed as surcharges at shift end.',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const MealTriggerModeComparison: React.FC<MealTriggerModeComparisonProps> = ({
  activeMode: controlledMode,
  onModeChange,
}) => {
  const [internalMode, setInternalMode] = useState<TriggerMode>('deadline');
  const activeMode = controlledMode ?? internalMode;
  const setActiveMode = (mode: TriggerMode) => {
    if (onModeChange) onModeChange(mode);
    else setInternalMode(mode);
  };

  return (
    <>
    <div role="group" aria-label="Surcharge trigger mode" className="my-2 flex items-stretch gap-2">
      {MODE_CARDS.map(({ mode, title, phrases }) => {
        const isActive = activeMode === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={isActive}
            onClick={() => setActiveMode(mode)}
            className={cn(
              'flex-1 flex flex-col text-left rounded-[0.25rem] border p-3 motion-safe:transition-colors cursor-pointer',
              'focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1 outline-none',
              isActive
                ? 'border-primary/45 bg-primary/[0.04]'
                : 'border-border/40 bg-surface-2 hover:border-border/60',
            )}
            style={isActive ? {
              boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.35), 0 0 8px color-mix(in srgb, var(--primary) 10%, transparent)',
              borderTopColor: 'color-mix(in srgb, var(--primary) 35%, transparent)',
            } : undefined}
          >
            {/* Tag */}
            <span
              className={cn(
                'inline-block rounded-[0.25rem] px-2 py-1 mb-2',
                'font-heading text-[10px] sm:text-[12px] uppercase tracking-[1.5px] leading-none font-bold',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-3 text-foreground/80',
              )}
            >
              {isActive ? 'Active' : 'Select'}
            </span>

            {/* Title */}
            <div
              className={cn(
                'font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] font-semibold mb-2',
                isActive ? 'text-foreground' : 'text-foreground/85',
              )}
            >
              {title}
            </div>

            {/* Contract phrases */}
            <ul className="space-y-1">
              {phrases.map((phrase) => (
                <li
                  key={phrase}
                  className={cn(
                    'font-mono text-[10px] sm:text-[12px] italic leading-snug border-b border-border/10 pb-1',
                    isActive ? 'text-foreground/85' : 'text-foreground/75',
                  )}
                >
                  {phrase}
                </li>
              ))}
            </ul>

          </button>
        );
      })}
    </div>

    {/* Active mode summary — centered below both cards */}
    <div className="relative mt-2 px-4 grid overflow-hidden">
      <AnimatePresence mode="sync" initial={false}>
        <motion.p
          key={activeMode}
          className={cn('col-start-1 row-start-1', TIMELINE_ANNOTATION_BLOCK_CLASS, 'mt-0 mb-2')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
        >
          <MeasureTokenText
            text={MODE_CARDS.find((c) => c.mode === activeMode)?.summary ?? ''}
            labelPrefix="Meal Trigger"
          />
        </motion.p>
      </AnimatePresence>
    </div>
    </>
  );
};
